<template>
  <div class="grid grid-cols-2 gap-x-4">
    <div data-cy="contractor-individual-form-contractor-first-name">
      <MoleculeLabel
        :text="t('subscription.contractorInformation.form.individual.contractor.firstName.label')"
        label-for="contractorFirstName"
        class="mt-8 mb-1 font-medium"
      />
      <MoleculeInputText
        v-model="contractorFirstName"
        name="contractorFirstName"
        id="contractorFirstName"
        :placeholder="
          t('subscription.contractorInformation.form.individual.contractor.firstName.placeholder')
        "
        :input-state="
          isValidationAttempted && getInputState({ errorMessage: errors['contractor.firstName'] })
        "
        :helper="
          isValidationAttempted &&
          errors['contractor.firstName'] &&
          getHelperMessage({ errorMessage: errors['contractor.firstName'] })
        "
      />
    </div>

    <div data-cy="contractor-individual-form-contractor-last-name">
      <MoleculeLabel
        :text="t('subscription.contractorInformation.form.individual.contractor.lastName.label')"
        label-for="contractorLastName"
        class="mt-8 mb-1 font-medium"
      />
      <MoleculeInputText
        v-model="contractorLastName"
        name="contractorLastName"
        id="contractorLastName"
        :placeholder="
          t('subscription.contractorInformation.form.individual.contractor.lastName.placeholder')
        "
        :input-state="
          isValidationAttempted && getInputState({ errorMessage: errors['contractor.lastName'] })
        "
        :helper="
          isValidationAttempted &&
          errors['contractor.lastName'] &&
          getHelperMessage({ errorMessage: errors['contractor.lastName'] })
        "
      />
    </div>

    <div data-cy="contractor-individual-form-is-co-contractor-toggle" class="col-span-2">
      <MoleculeLabel
        :text="t('subscription.contractorInformation.form.individual.isCoContractor.label')"
        label-for="coContractorFirstName"
        class="mt-8 mb-1 font-medium"
      />
      <MoleculeToggle
        v-model="isCoContractor"
        :label="t('subscription.contractorInformation.form.individual.isCoContractor.placeholder')"
        class="text-cime-gray-darker"
        label-position="right"
      />
    </div>

    <template v-if="isCoContractor">
      <div data-cy="contractor-individual-form-co-contractor-first-name">
        <MoleculeLabel
          :text="
            t('subscription.contractorInformation.form.individual.coContractor.firstName.label')
          "
          label-for="coContractorFirstName"
          class="mt-8 mb-1 font-medium"
        />
        <MoleculeInputText
          v-model="coContractorFirstName"
          name="coContractorFirstName"
          id="coContractorFirstName"
          :placeholder="
            t(
              'subscription.contractorInformation.form.individual.coContractor.firstName.placeholder'
            )
          "
          :input-state="
            isValidationAttempted &&
            getInputState({ errorMessage: errors['coContractor.firstName'] })
          "
          :helper="
            isValidationAttempted &&
            errors['coContractor.firstName'] &&
            getHelperMessage({ errorMessage: errors['coContractor.firstName'] })
          "
        />
      </div>

      <div data-cy="contractor-individual-form-co-contractor-last-name">
        <MoleculeLabel
          :text="
            t('subscription.contractorInformation.form.individual.coContractor.lastName.label')
          "
          label-for="coContractorLastName"
          class="mt-8 mb-1 font-medium"
        />
        <MoleculeInputText
          v-model="coContractorLastName"
          name="coContractorLastName"
          id="coContractorLastName"
          :placeholder="
            t(
              'subscription.contractorInformation.form.individual.coContractor.lastName.placeholder'
            )
          "
          :input-state="
            isValidationAttempted &&
            getInputState({ errorMessage: errors['coContractor.lastName'] })
          "
          :helper="
            isValidationAttempted &&
            errors['coContractor.lastName'] &&
            getHelperMessage({ errorMessage: errors['coContractor.lastName'] })
          "
        />
      </div>
    </template>
  </div>

  <AtomDivider class="my-12" />

  <AtomText
    :text="t('subscription.contractorInformation.form.contactTitle')"
    font-style="font-semibold"
    font-size="text-lg"
    data-cy="contractor-individual-form-contact-title"
  />

  <div class="grid grid-cols-2 gap-x-4">
    <div data-cy="contractor-individual-form-contractor-email" class="col-span-2">
      <MoleculeLabel
        :text="t('subscription.contractorInformation.form.individual.contractor.email.label')"
        label-for="contractorEmail"
        class="mt-8 mb-1 font-medium"
      />
      <MoleculeInputText
        v-model="contractorEmail"
        name="contractorEmail"
        id="contractorEmail"
        :placeholder="
          t('subscription.contractorInformation.form.individual.contractor.email.placeholder')
        "
        :input-state="
          isValidationAttempted && getInputState({ errorMessage: errors['contractor.email'] })
        "
        :helper="
          isValidationAttempted &&
          errors['contractor.email'] &&
          getHelperMessage({ errorMessage: errors['contractor.email'] })
        "
      />
    </div>

    <div data-cy="contractor-individual-form-contractor-email-confirm" class="col-span-2">
      <MoleculeLabel
        :text="
          t('subscription.contractorInformation.form.individual.contractor.emailConfirm.label')
        "
        label-for="contractorEmailConfirm"
        class="mt-8 mb-1 font-medium"
      />
      <MoleculeInputText
        v-model="contractorEmailConfirm"
        name="contractorEmailConfirm"
        id="contractorEmailConfirm"
        :placeholder="
          t(
            'subscription.contractorInformation.form.individual.contractor.emailConfirm.placeholder'
          )
        "
        :input-state="
          isValidationAttempted && getInputState({ errorMessage: errors.contractorEmailConfirm })
        "
        :helper="
          isValidationAttempted &&
          errors.contractorEmailConfirm &&
          getHelperMessage({ errorMessage: errors.contractorEmailConfirm })
        "
      />
    </div>

    <div data-cy="contractor-individual-form-contractor-phone-number" class="col-span-2">
      <MoleculeLabel
        :text="t('subscription.contractorInformation.form.individual.contractor.phoneNumber.label')"
        label-for="contractorPhoneNumber"
        class="mt-8 mb-1 font-medium"
      />
      <MoleculeInputPhone
        v-model="contractorPhoneNumber"
        name="contractorPhoneNumber"
        id="contractorPhoneNumber"
        :indicators="indicators"
        :placeholder="
          t('subscription.contractorInformation.form.individual.contractor.phoneNumber.placeholder')
        "
        :input-state="
          isValidationAttempted && getInputState({ errorMessage: errors['contractor.phoneNumber'] })
        "
        :helper="
          isValidationAttempted &&
          errors['contractor.phoneNumber'] &&
          getHelperMessage({ errorMessage: errors['contractor.phoneNumber'] })
        "
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { useForm } from 'vee-validate';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import useInputState from '@/composables/useInputState.composable';
import { useSubscriptionStore, useToastSnackbarStore } from '@/stores';

import type { SubscriptionIndividualContractor } from '@/types/subscription.type';
import { phoneIndicators } from '@/utils/const.util';
import { getErrorToasterConfig } from '@/utils/error.util';
import { formatPhoneNumber } from '@/utils/format.util';
import { emailRegex, firstNameRegex, lastNameRegex, phoneRegex } from '@/utils/regex.util';

import { EStorageKey } from '@/constants/storateKeys.constants';
import { useSessionStorage } from '@vueuse/core';

const { t } = useI18n();
const toastSnackBar = useToastSnackbarStore();
const subscriptionStore = useSubscriptionStore();
const { getInputState, getHelperMessage } = useInputState();

const { currentSubscription } = storeToRefs(subscriptionStore);

const contractorInformations = useSessionStorage<{ contractorEmail: string }>(
  EStorageKey.CONTRACTOR_INFORMATIONS,
  { contractorEmail: '' }
);

const isCoContractor = ref(!!currentSubscription.value.individualContractor?.coContractor);
const isValidationAttempted = ref(false);
const indicators = ref(phoneIndicators);

const contractorIndividualSchema = yup.object({
  contractor: yup.object({
    email: yup
      .string()
      .required(t('common.forms.required'))
      .trim()
      .matches(emailRegex, t('common.forms.format.email')),
    firstName: yup
      .string()
      .required(t('common.forms.required'))
      .matches(firstNameRegex, t('common.forms.format.name')),
    lastName: yup
      .string()
      .required(t('common.forms.required'))
      .matches(lastNameRegex, t('common.forms.format.name')),
    phoneNumber: yup
      .string()
      .required(t('common.forms.required'))
      .matches(phoneRegex, t('common.forms.format.phoneNumber')),
  }),
  coContractor: yup.object({
    firstName: yup.string().nullable(),
    lastName: yup.string().nullable(),
  }),
  contractorEmailConfirm: yup
    .string()
    .required(t('common.forms.required'))
    .test('email-match', t('common.forms.emailConfirmMismatch'), function (value) {
      return value === this.parent.contractor?.email;
    }),
});

type IndividualContractorForm = SubscriptionIndividualContractor & {
  contractorEmailConfirm: string;
};

const initialEmail =
  currentSubscription.value.individualContractor?.contractor.email ||
  currentSubscription.value.companyContractor?.contact.email ||
  contractorInformations.value.contractorEmail;

const {
  errors,
  defineField,
  validate: validateIndividualContractorForm,
} = useForm<IndividualContractorForm>({
  validationSchema: contractorIndividualSchema,
  initialValues: {
    contractor: {
      email: initialEmail,
      firstName:
        currentSubscription.value.individualContractor?.contractor.firstName ||
        currentSubscription.value.companyContractor?.contact.firstName,
      lastName:
        currentSubscription.value.individualContractor?.contractor.lastName ||
        currentSubscription.value.companyContractor?.contact.lastName,
      phoneNumber:
        currentSubscription.value.individualContractor?.contractor.phoneNumber?.replace(
          '+33',
          ''
        ) || currentSubscription.value.companyContractor?.contact.phoneNumber?.replace('+33', ''),
    },
    coContractor: {
      firstName: currentSubscription.value.individualContractor?.coContractor?.firstName,
      lastName: currentSubscription.value.individualContractor?.coContractor?.lastName,
    },
    contractorEmailConfirm: initialEmail,
  },
});

const [contractorEmail] = defineField('contractor.email');
const [contractorEmailConfirm] = defineField('contractorEmailConfirm');
const [contractorFirstName] = defineField('contractor.firstName');
const [contractorLastName] = defineField('contractor.lastName');
const [contractorPhoneNumber] = defineField('contractor.phoneNumber');
const [coContractorFirstName] = defineField('coContractor.firstName', {
  validateOnModelUpdate: false,
});
const [coContractorLastName] = defineField('coContractor.lastName', {
  validateOnModelUpdate: false,
});

const validateAndSubmit = async (): Promise<boolean> => {
  isValidationAttempted.value = true;
  const { valid: isFormValid } = await validateIndividualContractorForm();

  if (!isFormValid) {
    return false;
  }

  try {
    const contractorPayload: SubscriptionIndividualContractor = {
      contractor: {
        email: contractorEmail.value,
        firstName: contractorFirstName.value,
        lastName: contractorLastName.value,
        phoneNumber: formatPhoneNumber(contractorPhoneNumber.value),
      },
    };
    if (coContractorFirstName.value && coContractorLastName.value) {
      contractorPayload.coContractor = {
        firstName: coContractorFirstName.value,
        lastName: coContractorLastName.value,
      };
    }
    await subscriptionStore.updateSubscriptionWithIndividualContractor(contractorPayload);
    return true;
  } catch (error) {
    const toastConfig = getErrorToasterConfig({
      error,
      toasterId: 'contractor-informations',
      defaultMessage: t(
        'subscription.contractorInformation.api.errors.patchSubscriptionContractorIndividual'
      ),
    });
    toastSnackBar.addToast(toastConfig);
    return false;
  }
};

defineExpose({ validateAndSubmit });
</script>
